import React from 'react';
import { LinkIcon } from '../../../assets/LinkIcon';
import { DragIcon } from '../../../assets/DragIcon';

export interface ValueElement {
    id: string;
    imageUrl: string; 
    title: string;
    subtitle: string;
    url: string;
    isActive: boolean;
}

interface ValueRowProps {
    element: ValueElement;
    index: number;
    onChange: (id: string, field: keyof ValueElement, value: string | boolean) => void;
    onRemove: (id: string) => void;
    disabled: boolean;
    isUploading: boolean;
    onOpenIconPicker: () => void;
    isDragging?: boolean;
    onDragStart?: (idx: number, e: React.DragEvent) => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDrop?: (idx: number, e: React.DragEvent) => void;
    onDragEnd?: () => void;
    onTouchStart?: (idx: number) => void;
    onTouchMove?: (e: React.TouchEvent) => void;
    onTouchEnd?: () => void;
}

const ValueRow: React.FC<ValueRowProps> = ({ 
    element, 
    index,
    onChange, 
    onRemove, 
    disabled, 
    isUploading, 
    onOpenIconPicker,
    isDragging,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    onTouchStart,
    onTouchMove,
    onTouchEnd
}) => {
    return (
        <div 
            className={`value-row-container ${!element.isActive ? 'value-row-inactive' : ''} ${isDragging ? 'is-dragging' : ''}`}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop?.(index, e)}
            data-index={index}
        >
            <div className="value-row-header">
                <div className="d-flex align-items-center gap-3">
                    <div 
                        className="drag-handle"
                        draggable={!disabled && !isUploading}
                        onDragStart={(e) => onDragStart?.(index, e)}
                        onDragEnd={onDragEnd}
                        onTouchStart={() => onTouchStart?.(index)}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <DragIcon />
                    </div>
                    <span className="element-number">Element #{index + 1}</span>
                </div>
                <div className="value-row-actions">
                    <div className="custom-control custom-switch">
                        <input 
                            type="checkbox" 
                            className="custom-control-input" 
                            id={`toggle-${element.id}`} 
                            checked={element.isActive} 
                            onChange={(e) => onChange(element.id, 'isActive', e.target.checked)}
                            disabled={disabled || isUploading}
                        />
                        <label className="custom-control-label" htmlFor={`toggle-${element.id}`}>
                            {element.isActive ? 'Active' : 'Inactive'}
                        </label>
                    </div>
                    <button 
                        className="btn-remove-element" 
                        onClick={() => onRemove(element.id)}
                        disabled={disabled || isUploading}
                        title="Remove Element"
                    >
                        <span className="icoTrash" />
                    </button>
                </div>
            </div>

            <div className="value-row-body">
                <div className="row">
                    {/* Icon Selection */}
                    <div className="col-md-3">
                        <div className="field-group">
                            <p className="field-label"><b>Icon</b></p>
                            <div className="icon-selector-wrapper">
                                <div className="icon-preview-box">
                                    {isUploading ? (
                                        <div className="upload-spinner-small"></div>
                                    ) : element.imageUrl ? (
                                        <img src={element.imageUrl} alt="Icon" />
                                    ) : (
                                        <div className="no-icon-placeholder">No Icon</div>
                                    )}
                                </div>
                                <button 
                                    className="btn btn-select-icon" 
                                    onClick={onOpenIconPicker}
                                    disabled={disabled || isUploading}
                                >
                                    {element.imageUrl ? 'Change Icon' : 'Select Icon'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content Inputs */}
                    <div className="col-md-9">
                        <div className="row">
                            <div className="col-sm-6">
                                <div className="field-group">
                                    <p className="field-label"><b>Title</b></p>
                                    <input 
                                        value={element.title} 
                                        onChange={(e) => onChange(element.id, 'title', e.target.value)} 
                                        type="text" 
                                        className="input-round form-control" 
                                        placeholder="e.g. Free Shipping" 
                                        disabled={disabled || isUploading}
                                    />
                                </div>
                            </div>
                            <div className="col-sm-6">
                                <div className="field-group">
                                    <p className="field-label"><b>Subtitle</b></p>
                                    <input 
                                        value={element.subtitle} 
                                        onChange={(e) => onChange(element.id, 'subtitle', e.target.value)} 
                                        type="text" 
                                        className="input-round form-control" 
                                        placeholder="e.g. On all orders over £100" 
                                        disabled={disabled || isUploading}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-12">
                                <div className="field-group">
                                    <p className="field-label"><b>Link (Optional)</b></p>
                                    <div className="input-with-icon">
                                        <LinkIcon />
                                        <input 
                                            value={element.url || ''} 
                                            onChange={(e) => onChange(element.id, 'url', e.target.value)} 
                                            type="text" 
                                            className="input-round form-control" 
                                            placeholder="https://" 
                                            disabled={disabled || isUploading}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ValueRow;